import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  CalendarIcon,
  Download,
  FileText,
  Sparkles,
  Loader2, AlertTriangle, Eye,
  TrendingDownIcon,
  DollarSignIcon,
  Building2Icon,
  AlertTriangleIcon,
  CheckCircle2,
  TrendingUp,
  Info
} from "lucide-react";

import { format, startOfWeek, endOfWeek } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { useCompset } from "@/contexts/CompsetContext";
import { reportServices } from "@/services/dashboard.service";
import { formatCurrency, formatCurrencyPDF } from "../../helpers/formatters";
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { usePlans } from "@/contexts/PlanRulesContext";
import { loadFontAsBase64 } from "@/helpers/loadFontAsBase64";
import { generateAIPDF, generateBasicPDF } from "@/helpers/pdfGenerators";
import { Tooltip, TooltipProvider } from "../ui/tooltip";


export default function ReportsGenerator() {
  const { activeCompset } = useCompset();

  const { canAccessAiInsights } = usePlans();

  const [reportType, setReportType] = useState("daily");
  const [reportMode, setReportMode] = useState("basic"); //ai
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [customRange, setCustomRange] = useState({
    from: new Date(),
    to: new Date(),
  });

  const [reportData, setReportData] = useState(null);
  const [currency, setCurrency] = useState("");
  const reportRef = useRef(null);
  const reportRootRef = useRef(null);


  // -------------------------
  // Date Range Logic
  // -------------------------
  const getDateRange = () => {
    switch (reportType) {
      case "daily":
        return { start: selectedDate, end: selectedDate };
      // case "weekly":
      //   return {
      //     start: startOfWeek(selectedDate),
      //     end: endOfWeek(selectedDate),
      //   };
      // case "custom":
      //   return { start: customRange.from, end: customRange.to };
      // default:
      //   return { start: selectedDate, end: selectedDate };
    }
  };

  // -------------------------
  // React Query Mutation
  // -------------------------
  const generateReportMutation = useMutation({
    mutationFn: reportServices.generateReport,
    onSuccess: (data) => {
      setReportData(data);
      setCurrency(data?.currency);
      toast.success("Report generated successfully!");
    },
    onError: (error) => {
      console.error(error)
      // apiErrorHandler(error);
    },
  });

  const handleGenerateReport = () => {
    const range = getDateRange();
    generateReportMutation.mutate({
      start: range.start,
      end: range.end,
      reportType,
      r_id: activeCompset?.compset_id
    });
  };

  return (
    <div className="space-y-6" ref={reportRootRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports & Intelligence</h1>
          <p className="text-muted-foreground">
            Generate comprehensive market intelligence reports
          </p>
        </div>
        <Badge variant="outline" className="gap-1.5 px-3 py-1.5">
          <FileText className="h-3.5 w-3.5" />
          PDF Export Ready
        </Badge>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Report Configuration</CardTitle>
          <CardDescription>Select the time period and generate your report</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">

            {/* Report Type */}
            {/* <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
              </SelectContent>
            </Select> */}

            {/* Date Picker */}
            {reportType !== "custom" ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-48 justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                    {format(selectedDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <div className="flex flex-wrap gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full sm:w-auto">
                      {format(customRange.from, "PP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <Calendar
                      mode="single"
                      selected={customRange.from}
                      onSelect={(date) => setCustomRange((prev) => ({ ...prev, from: date }))}
                    />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full sm:w-auto">
                      {format(customRange.to, "PP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <Calendar
                      mode="single"
                      selected={customRange.to}
                      onSelect={(date) => setCustomRange((prev) => ({ ...prev, to: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Generate */}
            <Button
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
              className="gap-2 w-full sm:w-auto"
            >
              {generateReportMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Preview */}
      {reportData && (
        <div ref={reportRef} className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Card
              className={`cursor-pointer transition-all duration-200 ${reportMode === 'basic' ? 'border-primary ring-2 ring-primary/20 shadow-md' : 'hover:border-muted-foreground/30'}`}
              onClick={() => setReportMode('basic')}
              data-testid="select-basic-report"
            >
              <CardContent className="p-5 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${reportMode === 'basic' ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                  <FileText className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground">Basic Report</h3>
                  <p className="text-xs text-muted-foreground truncate">Executive Summary, Rate Parity, Competitors, Amenities, Events</p>
                </div>
                {reportMode === 'basic' && (
                  <Button size="sm" className="gap-2 flex-shrink-0" onClick={(e) => { e.stopPropagation(); generateBasicPDF(reportData, activeCompset?.currency); }} data-testid="download-basic-pdf">
                    <Download className="h-4 w-4" /> PDF
                  </Button>
                )}
              </CardContent>
            </Card>

            {
              canAccessAiInsights() && (
                <Card
                  className={`cursor-pointer transition-all duration-200 ${reportMode === 'ai' ? 'border-accent ring-2 ring-accent/20 shadow-md bg-accent/5' : 'hover:border-muted-foreground/30'}`}
                  onClick={() => {
                    setReportMode('ai')
                    if (reportRef.current) {

                      setTimeout(() => {
                        window.scrollTo({
                          top: reportRef.current?.clientHeight,
                          behavior: 'smooth'
                        });
                      }, 0)
                    }
                  }}
                  data-testid="select-ai-report"
                >
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${reportMode === 'ai' ? 'bg-accent text-white' : 'bg-accent/10 text-accent'}`}>
                      <Sparkles className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground">AI-Powered Report</h3>
                      <p className="text-xs text-muted-foreground truncate">Everything in Basic + AI Insights & Pricing Recommendations</p>
                    </div>
                    {reportMode === 'ai' && (
                      <Button size="sm" className="gap-2 flex-shrink-0 bg-accent hover:bg-accent/90" onClick={(e) => { e.stopPropagation(); generateAIPDF(reportData, activeCompset?.currency); }} data-testid="download-ai-pdf">
                        <Download className="h-4 w-4" /> PDF
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )
            }
          </div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Executive Summary</CardTitle>
                  <CardDescription>
                    {reportData?.dateRange?.start} to {reportData?.dateRange?.end} ({reportData?.daysAnalyzed} days)
                  </CardDescription>
                </div>
                <Badge variant="secondary">{reportType[0]?.charAt(0)?.toUpperCase() + reportType?.slice(1)} Report</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSignIcon className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Your Price</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(reportData?.yourHotel?.avgPrice, currency)}</p>
                  <p className={`text-xs ${reportData?.yourHotel?.priceVsMarket > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                    {reportData?.yourHotel?.priceVsMarket > 0 ? '+' : ''}{reportData?.yourHotel?.priceVsMarket}% vs market
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDownIcon className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm text-muted-foreground">Min Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(reportData?.yourHotel?.minPrice, currency)}</p>
                  <p className="text-xs text-muted-foreground">Lowest available</p>
                </div>
                <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2Icon className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-muted-foreground">Position</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">#{reportData?.market?.position}</p>
                  <p className="text-xs text-muted-foreground">of {reportData?.market?.totalCompetitors} competitors</p>
                </div>
                <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangleIcon className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-muted-foreground">Undercuts</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">{reportData?.rateParity?.totalViolations}</p>
                  <p className="text-xs text-muted-foreground">Rate parity issues</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* OTA Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">OTA Channel Performance</CardTitle>
                <CardDescription>Price comparison across distribution channels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {reportData?.otaSummary?.map((ota, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                          {index + 1}
                        </span>
                        <span className="text-sm font-medium">{ota?.ota}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm">{formatCurrency(ota?.avgPrice, currency)}</span>
                        <Badge
                          variant="outline"
                          className={ota?.violations > 0 ? 'bg-red-500/10 text-red-500 border-red-500/30' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'}
                        >
                          {ota?.violations > 0 ? `${ota?.violations} undercuts` : 'Compliant'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Competitor Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Competitor Intelligence</CardTitle>
                <CardDescription>Pricing summary of your compset</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {reportData?.competitorSummary?.map((comp, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium 'bg-muted text-muted-foreground'
                          }`}>
                          {index + 1}
                        </span>
                        <span className="text-sm font-medium">{comp?.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{comp?.avgPrice ? formatCurrency(comp?.avgPrice, currency) : "Sold Out"}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(comp?.minPrice, currency)} - {formatCurrency(comp?.maxPrice, currency)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Amenities Comparison */}
            <div className="flex gap-4 flex-col lg:col-span-2">
              {reportData?.amenitySummary?.length > 0 ? (
                <Card className="w-full flex-1">
                  <CardHeader>
                    <CardTitle className="text-lg">Amenities Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Hotel</TableHead>
                          {Object.keys(reportData?.amenitySummary[0]?.scores || []).concat([""]).concat("Value").map(h => (
                            <TableHead key={h} className="text-center">{h}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>

                      <TableBody>
                        {reportData?.amenitySummary?.map((a, i) => (
                          <TableRow
                            key={i}
                            className={a?.isClient ? 'bg-primary/5 font-medium' : ''}
                          >
                            {/* Hotel Name */}
                            <TableCell className="font-medium">
                              {a?.hotel?.length > 40 ? `${a?.hotel?.substring(0, 40)}...` : a?.hotel}
                            </TableCell>

                            {/* Category Scores */}
                            {Object.keys(a?.scores)?.map(cat => {
                              const score = a?.scores?.[cat];

                              return (
                                <TableCell key={cat} className="text-center">
                                  <span
                                    className={
                                      score >= 75
                                        ? 'text-emerald-600'
                                        : score >= 50
                                          ? 'text-amber-500'
                                          : 'text-red-500'
                                    }
                                  >
                                    {score ?? '-'}
                                  </span>
                                </TableCell>
                              );
                            })}

                            {/* Avg Score */}
                            <TableCell className="text-center text-blue-400 font-semibold">
                              {"more..."}
                            </TableCell>
                            <TableCell className="text-center font-semibold">
                              {a?.avgScore ?? '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )
                : null
              }

              {/* Demand & Events */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Demand & Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reportData.eventsSummary.map((event, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <span className="text-sm font-medium">{event.event}</span>
                            <p className="text-xs text-muted-foreground">{event.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={event.impact === 'High' ? 'bg-red-500/10 text-red-500 border-red-500/30' : 'bg-amber-500/10 text-amber-600 border-amber-500/30'}>
                            {event.impact}
                          </Badge>
                          <span className="text-xs text-muted-foreground hidden sm:block">{event.recommendation}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>


          {

            (reportData?.insights?.length > 0 && reportMode == "ai")
              ? (

                <Card className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border-primary/20">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-accent" />
                      <CardTitle className="text-lg">AI-Powered Insights & Recommendations</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {reportData?.insights?.map((insight, index) => (
                        <div key={index} className={`p-4 rounded-lg border ${insight.type === 'critical' ? 'bg-red-500/5 border-red-500/20' :
                          insight.type === 'warning' ? 'bg-amber-500/5 border-amber-500/20' :
                            insight.type === 'success' ? 'bg-emerald-500/5 border-emerald-500/20' :
                              'bg-blue-500/5 border-blue-500/20'
                          }`}>
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${insight.type === 'critical' ? 'bg-red-500/10' :
                              insight.type === 'warning' ? 'bg-amber-500/10' :
                                insight.type === 'success' ? 'bg-emerald-500/10' :
                                  'bg-blue-500/10'
                              }`}>
                              {insight.type === 'critical' ? <AlertTriangle className="h-4 w-4 text-red-500" /> :
                                insight.type === 'warning' ? <AlertTriangle className="h-4 w-4 text-amber-500" /> :
                                  insight.type === 'success' ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> :
                                    <TrendingUp className="h-4 w-4 text-blue-500" />}
                            </div>
                            <div>
                              <h4 className="font-semibold text-foreground mb-1">{insight.title}</h4>
                              <p className="text-sm text-muted-foreground">{insight.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )
              : reportMode === "ai"
                ? <div className="flex gap-3 bg-white p-3 rounded-md items-center">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>

                  <div>
                    <p className="text-sm text-blue-700 mt-1">
                      Generate AI insights to include them in this report.
                    </p>
                  </div>
                </div>
                : null
          }
        </div>
      )}

      {!reportData && !generateReportMutation.isPending && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">No Report Generated</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
              Generate Reportto view your comprehensive market intelligence analysis.
            </p>
          </CardContent>
        </Card>
      )}

    </div>
  );
}